import React, { useState, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Button, Modal, Space } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { normalizeTermsForDisplay } from '../utils/termsAndConditionsDisplay';

const RichTextEditor = ({ 
  value, 
  onChange, 
  onUserInput,
  placeholder = "Enter content...",
  label = "Content",
  previewTitle = "Preview",
  style = {},
  ...props 
}) => {
  const [previewVisible, setPreviewVisible] = useState(false);
  const quillRef = useRef();
  const safeValue = normalizeTermsForDisplay(value);

  // Quill modules configuration
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link'],
      [{ 'align': [] }],
      ['clean']
    ],
  };

  // Quill formats
  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'link', 'align'
  ];

  // Convert HTML to plain text for better readability in preview
  const stripHtml = (html) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  return (
    <div style={style}>
      <div style={{ marginBottom: '12px' }}>
        <Space>
          <span style={{ fontWeight: '500' }}>{label}</span>
          <Button 
            type="text" 
            icon={<EyeOutlined />} 
            size="small"
            onClick={() => setPreviewVisible(true)}
            disabled={!safeValue || stripHtml(safeValue).trim().length === 0}
            style={{ color: '#1890ff' }}
          >
            Preview
          </Button>
        </Space>
      </div>

      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={safeValue}
        onChange={(content, _delta, source) => {
          if (typeof onChange === 'function') onChange(content);
          if (source === 'user' && typeof onUserInput === 'function') {
            onUserInput(content);
          }
        }}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        style={{
          backgroundColor: '#fff',
          border: '1px solid #d9d9d9',
          borderRadius: '6px',
          ...props.style
        }}
        {...props}
      />

      {/* Preview Modal */}
      <Modal
        title={previewTitle}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
        style={{ top: 20 }}
      >
        <div style={{ 
          background: '#fafafa',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #f0f0f0',
          minHeight: '200px'
        }}>
          {safeValue ? (
            <div 
              dangerouslySetInnerHTML={{ __html: safeValue }}
              style={{
                fontSize: '14px',
                lineHeight: '1.6',
                color: '#262626',
                whiteSpace: 'pre-line'
              }}
            />
          ) : (
            <div style={{ 
              color: '#bfbfbf',
              fontStyle: 'italic',
              textAlign: 'center',
              padding: '40px'
            }}>
              No content to preview
            </div>
          )}
        </div>
        
        {/* HTML Source (for debugging) */}
        {safeValue && (
          <details style={{ marginTop: '16px' }}>
            <summary style={{ color: '#8c8c8c', cursor: 'pointer', fontSize: '12px' }}>
              View HTML Source
            </summary>
            <pre style={{ 
              background: '#f5f5f5',
              padding: '12px',
              borderRadius: '4px',
              fontSize: '11px',
              color: '#666',
              overflow: 'auto',
              maxHeight: '200px',
              marginTop: '8px'
            }}>
              {safeValue}
            </pre>
          </details>
        )}
      </Modal>
    </div>
  );
};

export default RichTextEditor;
